import { useState, useEffect, useRef } from "react";
import { Alert, Button, Spinner, Form } from "react-bootstrap";
import { listData } from "../services/api";
import { format } from "date-fns";
import ContentTable from "../../../components/ContentTable";
import DefaultRow from "../../../components/ContentTable/defaultRow";
import { useNavigate } from "react-router-dom";

export default function Registers() {
  const navigate = useNavigate();

  const [registers, setRegisters] = useState({
    headers: [
      "Name",
      "Email",
      "Birth Date",
      "Phone",
      "Address",
      "Last Updated",
    ],
    rows: [],
    data: [],
    count: 0,
  });
  const [isLoading, setIsLoading] = useState(true);
  const [isFiltering, setIsFiltering] = useState(false);
  const [error, setError] = useState("");
  const [cursor, setCursor] = useState("");
  const [hasMore, setHasMore] = useState(true);

  const [nameInput, setNameInput] = useState("");
  const [emailInput, setEmailInput] = useState("");
  const [filterName, setFilterName] = useState("");
  const [filterEmail, setFilterEmail] = useState("");

  const isFirstRender = useRef(true);

  async function loadRegisters(nextCursor = "") {
    setIsLoading(true);
    setError("");

    const result = await listData({
      table: "registers",
      term: "",
      value: "",
      limit: 20,
      cursor: nextCursor,
      filterName: filterName || undefined,
      filterEmail: filterEmail || undefined,
    });

    if (result instanceof Error) {
      setError("Erro ao buscar registros. Tente novamente.");
      setIsLoading(false);
      setIsFiltering(false);
      return;
    }

    const { data } = result;

    const newCursor = data.find((item) => item.cursor);
    setCursor(newCursor ? newCursor.cursor : "");

    const newRows = data
      .filter((item) => !item.cursor)
      .map((item) => [
        item.name,
        item.email,
        item.birth_date,
        item.phone_number,
        item.address,
        format(new Date(item.updated_at), "dd/MM/yyyy HH:mm"),
      ]);

    setRegisters((prev) => ({
      ...prev,
      rows: nextCursor ? (
        <DefaultRow args={[...prev.data, ...newRows]} />
      ) : (
        <DefaultRow args={newRows} />
      ),
      data: nextCursor ? [...prev.data, ...newRows] : newRows,
      count: data.find((item) => item.count)?.count || data.length,
    }));

    setHasMore(!!newCursor);
    setIsLoading(false);
    setIsFiltering(false);
  }

  useEffect(() => {
    loadRegisters();
  }, []);

  useEffect(() => {
    if (isFirstRender.current) {
      isFirstRender.current = false;
      return;
    }
    setCursor("");
    setHasMore(true);
    loadRegisters();
    // eslint-disable-next-line
  }, [filterName, filterEmail]);

  useEffect(() => {
    if (!hasMore || isLoading) return;
    const handleScroll = () => {
      if (
        window.innerHeight + document.documentElement.scrollTop + 100 >=
        document.documentElement.offsetHeight
      ) {
        setIsLoading(true);
        loadRegisters(cursor);
      }
    };
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
    // eslint-disable-next-line
  }, [cursor, hasMore, isLoading]);

  function applyFilters() {
    setIsFiltering(true);
    setFilterName(nameInput.trim());
    setFilterEmail(emailInput.trim());
  }

  function clearFilters() {
    setNameInput("");
    setEmailInput("");
    setIsFiltering(true);
    setFilterName("");
    setFilterEmail("");
  }

  function handleKeyDown(e) {
    if (e.key === "Enter") {
      applyFilters();
    }
  }

  return (
    <>
      <div className="w-full h-20 text-center content-center">
        <span className="w-full text-black fw-bold text-2xl tracking-widest uppercase">
          Cadastros
        </span>
      </div>
      <Button
        className="absolute right-10 top-20 w-20 h-10"
        onClick={() => navigate(-1)}
        variant="outline-primary"
      >
        Back
      </Button>

      <div className="flex flex-wrap items-end gap-3 mb-4 px-3">
        <Form.Group>
          <Form.Label className="text-sm text-muted mb-1">Nome</Form.Label>
          <Form.Control
            type="text"
            placeholder="Filtrar por nome"
            value={nameInput}
            onChange={(e) => setNameInput(e.target.value)}
            onKeyDown={handleKeyDown}
            disabled={isFiltering}
          />
        </Form.Group>
        <Form.Group>
          <Form.Label className="text-sm text-muted mb-1">Email</Form.Label>
          <Form.Control
            type="text"
            placeholder="Filtrar por email"
            value={emailInput}
            onChange={(e) => setEmailInput(e.target.value)}
            onKeyDown={handleKeyDown}
            disabled={isFiltering}
          />
        </Form.Group>
        <Button variant="primary" onClick={applyFilters} disabled={isFiltering}>
          {isFiltering ? (
            <>
              <Spinner size="sm" className="me-2" />
              Buscando...
            </>
          ) : (
            "Filtrar"
          )}
        </Button>
        {(filterName || filterEmail) && (
          <Button
            variant="outline-secondary"
            onClick={clearFilters}
            disabled={isFiltering}
          >
            Limpar
          </Button>
        )}
      </div>

      {error && (
        <Alert
          variant="danger"
          className="mx-3"
          dismissible
          onClose={() => setError("")}
        >
          {error}
        </Alert>
      )}

      <ContentTable args={registers} />
      {isLoading && (
        <div className="w-full text-center py-5">
          <Spinner />
        </div>
      )}
      {!hasMore && !isLoading && (
        <div className="w-full text-center py-3 text-muted">
          Fim dos registros
        </div>
      )}
    </>
  );
}
